import { Box, Chip, TextField } from "@mui/material";
import { type KeyboardEvent, useState } from "react";

interface ChipInputProps {
    field: {
        value: string[];
        onChange: (value: string[]) => void;
        onBlur?: () => void;
    };
    error?: boolean;
    helperText?: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;

    allowDuplicates?: boolean;
    duplicateMessage?: string;
    validateChip?: (value: string) => string | true;

    maxChips?: number;
    maxChipsMessage?: string;
}

const ChipInput = ({
    field,
    error,
    helperText,
    label,
    placeholder,
    required,
    disabled,
    duplicateMessage,
    allowDuplicates = false,
    validateChip,
    maxChips,
    maxChipsMessage,
}: ChipInputProps) => {
    const [inputValue, setInputValue] = useState("");
    const [localError, setLocalError] = useState("");

    const chips = Array.isArray(field.value)
        ? field.value
        : [];

    const addChip = () => {
        const value = inputValue.trim();

        if (!value) {
            setInputValue("");
            return;
        }

        const result = validateChip?.(value);

        if (result !== undefined && result !== true) {
            setLocalError(result);
            return;
        }

        const alreadyExists = chips.some(
            (chip) => chip.toLowerCase() === value.toLowerCase()
        );

        if (!allowDuplicates && alreadyExists) {
            setLocalError(duplicateMessage ?? "Already added");
            return;
        }

        if (maxChips && chips.length >= maxChips) {
            setLocalError(
                maxChipsMessage ?? `Maximum ${maxChips} items allowed`
            );
            return;
        }

        field.onChange([...chips, value]);
        setInputValue("");
        setLocalError("");
    };

    const removeChip = (chip: string) => {
        field.onChange(chips.filter((item) => item !== chip));
        setLocalError("");
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addChip();
        }
    };

    return (
        <Box>
            <TextField
                fullWidth
                variant="standard"
                label={label}
                placeholder={
                    maxChips !== undefined && chips.length >= maxChips
                        ? `Maximum ${maxChips} items added`
                        : placeholder
                }
                required={required}
                disabled={disabled}
                value={inputValue}
                error={error || !!localError}
                helperText={
                    localError || helperText
                }
                onChange={(e) => {
                    setInputValue(e.target.value);

                    if (localError) {
                        setLocalError("");
                    }
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                    if (inputValue.trim()) {
                        addChip();
                    }
                    field.onBlur?.();
                }}
            />

            {chips.length > 0 && (
                <Box
                    mt={1.5}
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                >
                    {chips.map((chip) => (
                        <Chip
                            key={chip}
                            label={chip}
                            size="small"
                            onDelete={() => removeChip(chip)}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default ChipInput;